import { DateTime } from 'luxon';
import { BulkActivationResultItemDTO, CreateBenefitActivationDTO, EmployeeDTO, UpdateBenefitActivationDTO } from 'probonio-shared-ui/api';
import React from 'react';
import { BaseActivationDialog, BaseActivationDialogProps } from './BaseActivationDialog';
import { ActivationDialogState } from './useActivationDialogState';

export interface ActivationDialogExtensions<T extends object = {}> extends Pick<
  BaseActivationDialogProps<T>,
  'onResetBenefitOptions' | 'onCreateActivation' | 'onUpdateActivation'
> {
  Header?: React.ComponentType;
  CreateFields?: React.ReactNode;
  ChangeOptionsFields?: React.ReactNode;
  InlineField?: React.ComponentType;
  createLabel?: string;
  earliestStartDate?: () => DateTime;
  titleKey?: string;
  bulkTitleKey?: string;
  hideSaveButton?: () => boolean;
}

export interface ActivationDialogSaveFunctions {
  activeEndDate?: DateTime;
  onCreateActivation: (create: CreateBenefitActivationDTO) => void | Promise<void>;
  onUpdateActivation?: (benefitActivationId: string, update: UpdateBenefitActivationDTO) => void | Promise<void>;
  onAdjusted?: (result: BulkActivationResultItemDTO) => void;
}

export interface ActivationDialogProps {
  activationDialogState: ActivationDialogState;
  isLoading?: boolean;
  saveFunctions: ActivationDialogSaveFunctions;
  warnings?: React.ReactNode;
  /** the employee's termination date */
  terminationDate?: DateTime;
  employee?: EmployeeDTO;
  progress?: number;
}

interface Props<T extends object> extends ActivationDialogProps {
  activationDialogExtensions: ActivationDialogExtensions<T>;
}

export const ActivationDialog = <T extends object>({
  activationDialogState,
  isLoading,
  activationDialogExtensions,
  saveFunctions,
  warnings,
  terminationDate,
  employee,
  progress,
}: Props<T>): React.JSX.Element => {
  const Header = activationDialogExtensions.Header;
  const InlineField = activationDialogExtensions.InlineField;

  return (
    <BaseActivationDialog<T>
      activationDialogState={activationDialogState}
      isLoading={isLoading}
      activeEndDate={saveFunctions.activeEndDate}
      warnings={warnings}
      header={Header && <Header />}
      fields={activationDialogState.activation ? activationDialogExtensions.ChangeOptionsFields : activationDialogExtensions.CreateFields}
      inlineField={InlineField && <InlineField />}
      createLabel={activationDialogExtensions.createLabel}
      earliestStartDate={activationDialogExtensions.earliestStartDate}
      terminationDate={terminationDate}
      onResetBenefitOptions={activationDialogExtensions.onResetBenefitOptions}
      onCreateActivation={activationDialogExtensions.onCreateActivation}
      onUpdateActivation={activationDialogExtensions.onUpdateActivation}
      onSaveCreateBenefitActivationDTO={saveFunctions.onCreateActivation}
      onSaveUpdateBenefitActivationDTO={saveFunctions.onUpdateActivation}
      employee={employee}
      customTitleKey={employee ? activationDialogExtensions.titleKey : activationDialogExtensions.bulkTitleKey}
      progress={progress}
    />
  );
};
